/**
 * Standard pricing (USD, no sales tax / GST added). Positioned as a senior
 * offshore studio: clearly below US and Canadian agency rates, clearly above
 * template shops and marketplace freelancers. Market bands come from
 * published US agency prices and cost guides, researched 2026-10-19.
 * Edit numbers here only; the pricing page, state pages and FAQs read from it.
 */
export const PRICING_UPDATED = '2026';

export const PLANS = [
  {
    slug: 'landing-page',
    name: 'Landing page',
    bestFor: 'Campaigns & launches',
    from: '$1,200',
    range: '$1,200 – $2,500',
    timeline: '2–3 weeks',
    includes: ['1 long-form custom-designed page', 'Copy structure & conversion-focused layout', 'Lead form + calendar booking CTA', 'Analytics & ad pixel setup', 'Mobile-first, sub-2s load'],
    link: '/services/website-development/',
  },
  {
    slug: 'business-website',
    name: 'Business website',
    bestFor: 'SMBs, startups, manufacturers',
    from: '$2,900',
    range: '$2,900 – $7,500',
    timeline: '6–10 weeks',
    includes: ['5–15 custom-designed pages (no templates)', 'Easy CMS for pages, blog & news', 'On-page SEO, schema & sitemap', 'Contact, scheduling & Google Business integration', 'Spanish pages on request', '1 month post-launch support'],
    link: '/services/website-development/',
    featured: true,
  },
  {
    slug: 'ecommerce',
    name: 'Ecommerce store',
    bestFor: 'DTC brands & retailers',
    from: '$4,900',
    range: '$4,900 – $14,000 (Shopify) · custom from $14,000',
    timeline: '10–16 weeks',
    includes: ['Shopify, Hydrogen or custom headless build', 'Stripe, Shop Pay, Apple Pay & sales-tax automation', 'Catalog, filters & fast product pages', 'Shipping, SMS & email automations', 'Product & order import'],
    link: '/services/shopify-development/',
  },
  {
    slug: 'web-app',
    name: 'Web app / custom software',
    bestFor: 'Portals, dashboards, SaaS',
    from: '$9,900',
    range: '$9,900 – $39,000',
    timeline: '8–14 weeks',
    includes: ['Discovery sprint & written spec', 'Login, roles & admin panel', 'APIs & integrations (ERP, CRM, payments)', 'Automated tests & CI', 'Hosting setup & hand-over docs'],
    link: '/services/web-app-development/',
  },
  {
    slug: 'mobile-app',
    name: 'Mobile app',
    bestFor: 'iOS + Android',
    from: '$12,000',
    range: '$12,000 – $45,000',
    timeline: '10–16 weeks',
    includes: ['One codebase for iOS & Android (Flutter / React Native)', 'Backend, admin panel & push notifications', 'Payments & offline support', 'App Store & Play Store submission', 'Crash reporting & analytics'],
    link: '/services/mobile-app-development/',
  },
  {
    slug: 'ai',
    name: 'AI chatbot & automation',
    bestFor: 'Support, search, ops',
    from: '$6,500',
    range: '$6,500 – $26,000',
    timeline: '6–12 weeks',
    includes: ['AI assistant or document search (RAG) on your data', 'Website, SMS or internal tool integration', 'Evaluation set & guardrails', 'Usage cost limits & monitoring', 'Admin to update knowledge'],
    link: '/services/ai-solutions/',
  },
];

export const RETAINERS = [
  { name: 'Care', price: '$249 / month', body: 'Hosting checks, backups, security updates, uptime monitoring and up to 2 hours of edits a month.' },
  { name: 'Growth', price: '$799 / month', body: 'Everything in Care plus SEO health, Core Web Vitals, new pages and landing pages — up to 8 hours a month.' },
  { name: 'Product partner', price: 'From $2,400 / month', body: 'Dedicated design and engineering days for apps and stores that ship new features every month.' },
];

export const MARKET = {
  intro:
    'We looked at published prices from website builders, US digital agencies, Clutch and DesignRush listings, and cost guides from firms in Texas, Florida, California and New York. Prices vary 10–50× for the same “website” — mostly because of templates vs custom design, and who builds it.',
  rows: [
    { project: 'Basic business website', budget: '$500 – $2,500', mid: '$5,000 – $15,000', ours: 'from $2,900' },
    { project: 'Custom corporate website', budget: '—', mid: '$15,000 – $50,000', ours: '$2,900 – $7,500' },
    { project: 'Ecommerce (Shopify)', budget: '$1,500 – $5,000', mid: '$10,000 – $40,000', ours: '$4,900 – $14,000' },
    { project: 'Custom ecommerce', budget: '—', mid: '$40,000 – $150,000', ours: 'from $14,000' },
    { project: 'Custom web application', budget: '—', mid: '$50,000 – $250,000', ours: '$9,900 – $39,000' },
    { project: 'Mobile app (iOS + Android)', budget: '$5,000 – $25,000', mid: '$60,000 – $300,000', ours: '$12,000 – $45,000' },
    { project: 'Maintenance', budget: '$50 – $150 / mo', mid: '$500 – $2,500 / mo', ours: 'from $249 / mo' },
  ],
  note: 'Market ranges from publicly listed prices and cost guides of US agencies and website builders, October 2026. Budget prices usually mean a pre-made template, a bundled hosting plan and limited revisions. NioDelta prices are in US dollars and final — we are an Indian company, so no US sales tax is added to the invoice.',
};

export const COST_FACTORS = [
  { k: 'Template vs custom design', v: 'A $500 website reuses a theme thousands of others use. Custom design is built around your brand, content and buyers — it is the biggest single price difference in the market.' },
  { k: 'Number of pages & content', v: 'More pages means more design, copy and SEO work. We can write and structure copy for you, or work with your existing content.' },
  { k: 'Features & integrations', v: 'Payments, logins, booking, ERP / CRM sync, multi-language and dashboards each add build and testing time.' },
  { k: 'Platform', v: 'Shopify and CMS builds are faster to launch; custom React / Next.js builds cost more but load faster, scale further and avoid plugin bloat.' },
  { k: 'Speed & SEO requirements', v: 'Performance budgets, schema markup and migration redirects protect rankings. We include them by default rather than as add-ons.' },
  { k: 'Ongoing support', v: 'Hosting, security updates and small changes after launch. Budget quotes often leave this out and charge renewals later.' },
];

export const PRICING_FAQ = [
  { q: 'How much does website development cost in the US?', a: 'In the US, template and builder websites start around $500–$2,500, typical agency websites cost $5,000–$15,000, and custom corporate sites run $15,000–$50,000. With NioDelta a custom business website starts at $2,900 because our team is in India — same senior design and engineering, a third of the price.' },
  { q: 'Why are you cheaper than a US agency?', a: 'Our studio is in India, so our cost base is lower than a US agency paying US salaries and office rent. You are not buying junior work: the same senior designers and engineers who built our own products do your project. The saving is geography, not experience.' },
  { q: 'How much does ecommerce development cost?', a: 'US agencies publish $10,000–$40,000 for a Shopify store and $40,000–$150,000 for custom headless commerce. Our Shopify builds run $4,900–$14,000 and custom stores start at $14,000, with Stripe, Shop Pay, Apple Pay and sales-tax automation included.' },
  { q: 'Why are some websites only $500?', a: 'Very low prices usually mean a pre-made template, limited pages and revisions, bundled hosting and renewal fees. That can suit a personal page; for a business that needs leads, speed and search rankings, custom work pays for itself.' },
  { q: 'Do your prices include tax, domain and hosting?', a: 'Prices are in US dollars and final. We are an Indian company, so no US sales tax is added; we provide a W-8BEN-E on request. Domain and hosting are billed at cost in your name, so you always own them — typically $100–$400 a year for a business website.' },
  { q: 'How do payments work?', a: 'Fixed quote, usually 40% to start, 30% at design sign-off and 30% at launch. We invoice in USD and accept ACH, wire, credit card and Stripe. Larger apps are split into monthly milestones.' },
  { q: 'How much does mobile app development cost?', a: 'US app agencies quote $60,000–$300,000. A cross-platform iOS + Android app with backend and admin panel starts at $12,000 with us; most business apps land between $12,000 and $45,000 depending on features.' },
];
